import React from "react";
import axios from "axios";
import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { handleStaleClientError } from "./staleClient";
import { queryClient } from "./queryClient";

function toCamel(o: any) {
  var newO: any, origKey: any, newKey: any, value: any;

  if (o instanceof Array) {
    return o.map(function (value) {
      if (typeof value === "object") {
        value = toCamel(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in o) {
      if (o.hasOwnProperty(origKey) && typeof o[origKey] !== "undefined") {
        newKey =
          origKey === "__typename"
            ? origKey
            : origKey.replace(/_([a-z])/g, function (_a: string, b: string) {
                return b.toUpperCase();
              });
        value = o[origKey];
        if (value instanceof Array || (value !== null && value.constructor === Object)) {
          value = toCamel(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
}

function toSnake(o: any) {
  var newO: any, origKey: any, newKey: any, value: any;

  if (o instanceof Array) {
    return o.map(function (value) {
      if (typeof value === "object") {
        value = toSnake(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in o) {
      if (o.hasOwnProperty(origKey) && typeof o[origKey] !== "undefined") {
        newKey =
          origKey === "__typename"
            ? origKey
            : origKey.replace(/([A-Z])/g, function (a: string) {
                return "_" + a.toLowerCase();
              });
        value = o[origKey];
        if (value instanceof Array || (value !== null && value.constructor === Object)) {
          value = toSnake(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
}

type UseMutationHookResult<InputT, ResultT> = [
  (input: InputT) => Promise<ResultT | any>,
  { data: ResultT | null; loading: boolean; error: Error | null },
];

export function useMutation<InputT, ResultT>(
  fn: (input: InputT) => Promise<ResultT>,
): UseMutationHookResult<InputT, ResultT> {
  const [data, setData] = React.useState<ResultT | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);

  const execute = async (input: InputT): Promise<ResultT | any> => {
    try {
      setLoading(true);
      setError(null);

      var data = await fn(input);

      setData(data);

      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return [execute, { data, loading, error }];
}

export type CompanyId = string;

export type Id = string;

export type Json = string;

export interface Account {
  id: string;
  fullName: string;
  email: string;
  siteAdmin: boolean;
  companiesCount: number;
  ownedCompaniesCount: number;
  insertedAt: string;
}

export interface Activity {
  id?: string;
  action?: string;
  insertedAt?: string;
}

export interface BillingPlanDefinition {
  id: string;
  key: string;
  displayName: string;
  tierRank: number;
  billingBehavior: BillingBehavior;
  customerSelectable: boolean;
  archivedAt?: string | null;
  memberLimit?: number;
  storageLimitBytes?: number;
}

export interface BillingProduct {
  id: string;
  provider: string;
  planFamily: string;
  billingInterval: string;
  polarProductId: string;
  polarProductName: string;
  priceAmount: number;
  priceCurrency: string;
  version: number;
  active: boolean;
  archivedAt: string;
  lastSyncedAt: string;
  insertedAt: string;
  updatedAt: string;
}

export interface Company {
  id?: string;
  name?: string;
  owners?: Person[];
  peopleCount?: number;
  goalsCount?: number;
  spacesCount?: number;
  projectsCount?: number;
  storageUsageBytes?: number;
  lastActivityAt?: string;
  insertedAt?: string;
  uuid?: string;
  shortId?: string;
  enabledFeatures?: string[];
}

export interface CuratedTemplate {
  __typename: "curated_template";
  id: Id;
  type: CuratedTemplateType;
  state: CuratedTemplateState;
  title: string;
  slug: string;
  summary?: string | null;
  category?: string | null;
  contentLanguage: string;
  definition?: Json;
  previewData?: Json;
  publishedAt?: string | null;
  insertedAt: string;
  updatedAt: string;
}

export interface CuratedTemplateValidationError {
  path: string;
  message: string;
}

export interface EmailSettings {
  provider: EmailProvider;
  notificationEmail?: string;
  smtp?: SmtpSettings;
  sendgridApiKeySet?: boolean;
}

export interface Person {
  id: string;
  fullName: string;
  email: string;
  avatarUrl: string;
  title: string;
}

export interface SearchIndexRun {
  __typename: "search_index_run";
  id: string;
  kind: SearchIndexMaintenanceKind;
  status: SearchIndexRunStatus;
  phase: SearchIndexRunPhase;
  processedCount: number;
  insertedCount: number;
  updatedCount: number;
  unchangedCount: number;
  supersededCount: number;
  skippedCount: number;
  failedCount: number;
  deletedOrphanCount: number;
  lastError?: string;
  startedAt?: string;
  completedAt?: string;
  insertedAt: string;
}

export interface SearchIndexSourceStatus {
  sourceType: SearchIndexSourceType;
  latestRun?: SearchIndexRun;
}

export interface SiteMessage {
  id: string;
  title: string;
  description: string;
  allCompanies: boolean;
  active: boolean;
  expiresAt?: string;
  companyIds?: string[];
  insertedAt: string;
  updatedAt?: string;
}

export interface SmtpSettings {
  host: string;
  port: number;
  username: string;
  ssl: boolean;
  tlsRequired: boolean;
  smtpPasswordSet?: boolean;
}

export type BillingBehavior = "internal" | "provider_managed";

export type CuratedTemplateState = "draft" | "published";

export type CuratedTemplateType = "kpi" | "goal" | "project";

export type EmailProvider = "smtp" | "sendgrid";

export type SearchIndexMaintenanceKind = "backfill" | "reconciliation";

export type SearchIndexRunPhase = "source_scan" | "index_scan";

export type SearchIndexRunStatus = "pending" | "running" | "completed" | "completed_with_errors" | "failed";

export type SearchIndexSourceType =
  | "project"
  | "goal"
  | "milestone"
  | "task"
  | "person"
  | "discussion"
  | "project_check_in"
  | "goal_check_in"
  | "project_retrospective"
  | "resource_hub_document"
  | "resource_hub_folder"
  | "resource_hub_file"
  | "resource_hub_link";

export interface CuratedTemplatesGetInput {
  id: Id;
}

export interface CuratedTemplatesGetResult {
  template: CuratedTemplate;
}

export interface CuratedTemplatesListInput {
  type?: CuratedTemplateType;
  state?: CuratedTemplateState;
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CuratedTemplatesListResult {
  templates: CuratedTemplate[];
  total: number;
}

export interface GetAccountsInput {}

export interface GetAccountsResult {
  accounts: Account[];
}

export interface GetActiveCompaniesInput {}

export interface GetActiveCompaniesResult {
  companies: Company[];
}

export interface GetActivitiesInput {
  companyId: CompanyId;
}

export interface GetActivitiesResult {
  activities: Activity[];
}

export interface GetCompaniesInput {}

export interface GetCompaniesResult {
  companies: Company[];
}

export interface GetCompanyInput {
  id: CompanyId;
}

export interface GetCompanyResult {
  company: Company;
  availableFeatures: string[];
}

export interface GetEmailSettingsInput {}

export interface GetEmailSettingsResult {
  emailSettings: EmailSettings;
}

export interface GetSearchIndexStatusInput {}

export interface GetSearchIndexStatusResult {
  sources: SearchIndexSourceStatus[];
}

export interface GetUpdateBadgeSettingsInput {}

export interface GetUpdateBadgeSettingsResult {
  enabled: boolean;
}

export interface ListBillingPlanDefinitionsInput {}

export interface ListBillingPlanDefinitionsResult {
  planDefinitions: BillingPlanDefinition[];
}

export interface ListBillingProductsInput {}

export interface ListBillingProductsResult {
  products: BillingProduct[];
}

export interface ListSiteMessagesInput {}

export interface ListSiteMessagesResult {
  messages: SiteMessage[];
}

export interface ArchiveBillingPlanDefinitionInput {
  id: string;
}

export interface ArchiveBillingPlanDefinitionResult {
  planDefinition: BillingPlanDefinition;
}

export interface ArchiveBillingProductInput {
  id: string;
}

export interface ArchiveBillingProductResult {
  product: BillingProduct;
}

export interface CreateBillingPlanDefinitionInput {
  planKey: string;
  displayName: string;
  tierRank: number;
  billingBehavior: BillingBehavior;
  customerSelectable: boolean;
  memberLimit?: number | null;
  storageLimitBytes?: number | null;
}

export interface CreateBillingPlanDefinitionResult {
  planDefinition: BillingPlanDefinition;
}

export interface CreateBillingProductInput {
  planFamily: string;
  billingInterval: string;
  polarProductName: string;
  priceAmount: number;
  priceCurrency?: string;
}

export interface CreateBillingProductResult {
  product: BillingProduct;
}

export interface CreateSiteMessageInput {
  title: string;
  description: Json;
  allCompanies: boolean;
  active: boolean;
  expiresAt?: string;
  companyIds?: string[];
}

export interface CreateSiteMessageResult {
  message: SiteMessage;
}

export interface CuratedTemplatesCreateInput {
  type: CuratedTemplateType;
  title: string;
  slug: string;
  summary?: string | null;
  category?: string | null;
  contentLanguage: string;
  definition: Json;
  previewData?: Json;
}

export interface CuratedTemplatesCreateResult {
  template?: CuratedTemplate | null;
  errors: CuratedTemplateValidationError[];
}

export interface CuratedTemplatesDeleteInput {
  id: Id;
  expectedUpdatedAt: string;
}

export interface CuratedTemplatesDeleteResult {
  template?: CuratedTemplate | null;
  errors: CuratedTemplateValidationError[];
}

export interface CuratedTemplatesPublishInput {
  id: Id;
  expectedUpdatedAt: string;
}

export interface CuratedTemplatesPublishResult {
  template?: CuratedTemplate | null;
  errors: CuratedTemplateValidationError[];
}

export interface CuratedTemplatesUpdateInput {
  id: Id;
  expectedUpdatedAt: string;
  type: CuratedTemplateType;
  title: string;
  slug: string;
  summary?: string | null;
  category?: string | null;
  contentLanguage: string;
  definition: Json;
  previewData?: Json;
}

export interface CuratedTemplatesUpdateResult {
  template?: CuratedTemplate | null;
  errors: CuratedTemplateValidationError[];
}

export interface CuratedTemplatesUpdateMetadataInput {
  id: Id;
  expectedUpdatedAt: string;
  category?: string | null;
}

export interface CuratedTemplatesUpdateMetadataResult {
  template?: CuratedTemplate | null;
  errors: CuratedTemplateValidationError[];
}

export interface CuratedTemplatesValidateInput {
  type: CuratedTemplateType;
  title: string;
  slug: string;
  summary?: string | null;
  category?: string | null;
  contentLanguage: string;
  definition: Json;
  previewData?: Json;
}

export interface CuratedTemplatesValidateResult {
  valid: boolean;
  errors: CuratedTemplateValidationError[];
}

export interface DeleteAccountInput {
  accountId: string;
}

export interface DeleteAccountResult {
  success: boolean;
  error?: string;
  blockingCompanyNames?: string[];
}

export interface DeleteSiteMessageInput {
  id: string;
}

export interface DeleteSiteMessageResult {
  success: boolean;
}

export interface DemoteAccountFromSiteAdminInput {
  accountId: string;
}

export interface DemoteAccountFromSiteAdminResult {
  success: boolean;
  error?: string;
}

export interface DisableFeaturesInput {
  companyId: CompanyId;
  features: string[];
}

export interface DisableFeaturesResult {
  success: boolean;
}

export interface EnableFeatureInput {
  companyId: CompanyId;
  feature: string;
}

export interface EnableFeatureResult {
  success: boolean;
}

export interface PromoteAccountToSiteAdminInput {
  accountId: string;
}

export interface PromoteAccountToSiteAdminResult {
  success: boolean;
  error?: string;
}

export interface SendTestEmailInput {
  recipient: string;
  subject: string;
  body: string;
}

export interface SendTestEmailResult {
  success: boolean;
  error?: string;
}

export interface SetActiveBillingProductInput {
  id: string;
}

export interface SetActiveBillingProductResult {
  product: BillingProduct;
}

export interface StartSearchIndexMaintenanceInput {
  kind: SearchIndexMaintenanceKind;
  sourceType?: SearchIndexSourceType;
}

export interface StartSearchIndexMaintenanceResult {
  startedSourceTypes: SearchIndexSourceType[];
  alreadyRunningSourceTypes: SearchIndexSourceType[];
}

export interface SyncBillingProductsFromPolarInput {}

export interface SyncBillingProductsFromPolarResult {
  success: boolean;
  syncedCount: number;
}

export interface UnarchiveBillingPlanDefinitionInput {
  id: string;
}

export interface UnarchiveBillingPlanDefinitionResult {
  planDefinition: BillingPlanDefinition;
}

export interface UpdateBillingPlanDefinitionInput {
  id: string;
  displayName: string;
  tierRank: number;
  billingBehavior: BillingBehavior;
  customerSelectable: boolean;
  memberLimit?: number | null;
  storageLimitBytes?: number | null;
}

export interface UpdateBillingPlanDefinitionResult {
  planDefinition: BillingPlanDefinition;
}

export interface UpdateBillingProductInput {
  id: string;
  polarProductName?: string;
  priceAmount?: number;
  priceCurrency?: string;
}

export interface UpdateBillingProductResult {
  product: BillingProduct;
}

export interface UpdateEmailSettingsInput {
  provider: EmailProvider;
  notificationEmail?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpSsl?: boolean;
  smtpTlsRequired?: boolean;
  sendgridApiKey?: string;
}

export interface UpdateEmailSettingsResult {
  success: boolean;
  error?: string;
  emailSettings?: EmailSettings;
}

export interface UpdateSiteMessageInput {
  id: string;
  title?: string;
  description?: Json;
  allCompanies?: boolean;
  active?: boolean;
  expiresAt?: string;
  companyIds?: string[];
}

export interface UpdateSiteMessageResult {
  message: SiteMessage;
}

export interface UpdateUpdateBadgeSettingsInput {
  enabled: boolean;
}

export interface UpdateUpdateBadgeSettingsResult {
  success: boolean;
  enabled: boolean;
}

class ApiNamespaceCuratedTemplates {
  constructor(private client: ApiClient) {}

  async create(input: CuratedTemplatesCreateInput): Promise<CuratedTemplatesCreateResult> {
    return this.client.post("/curated_templates/create", input);
  }

  async delete(input: CuratedTemplatesDeleteInput): Promise<CuratedTemplatesDeleteResult> {
    return this.client.post("/curated_templates/delete", input);
  }

  async publish(input: CuratedTemplatesPublishInput): Promise<CuratedTemplatesPublishResult> {
    return this.client.post("/curated_templates/publish", input);
  }

  async update(input: CuratedTemplatesUpdateInput): Promise<CuratedTemplatesUpdateResult> {
    return this.client.post("/curated_templates/update", input);
  }

  async updateMetadata(input: CuratedTemplatesUpdateMetadataInput): Promise<CuratedTemplatesUpdateMetadataResult> {
    return this.client.post("/curated_templates/update_metadata", input);
  }

  async validate(input: CuratedTemplatesValidateInput): Promise<CuratedTemplatesValidateResult> {
    return this.client.post("/curated_templates/validate", input);
  }
}

class ApiNamespaceRoot {
  constructor(private client: ApiClient) {}

  async archiveBillingPlanDefinition(
    input: ArchiveBillingPlanDefinitionInput,
  ): Promise<ArchiveBillingPlanDefinitionResult> {
    return this.client.post("/archive_billing_plan_definition", input);
  }

  async archiveBillingProduct(input: ArchiveBillingProductInput): Promise<ArchiveBillingProductResult> {
    return this.client.post("/archive_billing_product", input);
  }

  async createBillingPlanDefinition(
    input: CreateBillingPlanDefinitionInput,
  ): Promise<CreateBillingPlanDefinitionResult> {
    return this.client.post("/create_billing_plan_definition", input);
  }

  async createBillingProduct(input: CreateBillingProductInput): Promise<CreateBillingProductResult> {
    return this.client.post("/create_billing_product", input);
  }

  async createSiteMessage(input: CreateSiteMessageInput): Promise<CreateSiteMessageResult> {
    return this.client.post("/create_site_message", input);
  }

  async deleteAccount(input: DeleteAccountInput): Promise<DeleteAccountResult> {
    return this.client.post("/delete_account", input);
  }

  async deleteSiteMessage(input: DeleteSiteMessageInput): Promise<DeleteSiteMessageResult> {
    return this.client.post("/delete_site_message", input);
  }

  async demoteAccountFromSiteAdmin(input: DemoteAccountFromSiteAdminInput): Promise<DemoteAccountFromSiteAdminResult> {
    return this.client.post("/demote_account_from_site_admin", input);
  }

  async disableFeatures(input: DisableFeaturesInput): Promise<DisableFeaturesResult> {
    return this.client.post("/disable_features", input);
  }

  async enableFeature(input: EnableFeatureInput): Promise<EnableFeatureResult> {
    return this.client.post("/enable_feature", input);
  }

  async promoteAccountToSiteAdmin(input: PromoteAccountToSiteAdminInput): Promise<PromoteAccountToSiteAdminResult> {
    return this.client.post("/promote_account_to_site_admin", input);
  }

  async sendTestEmail(input: SendTestEmailInput): Promise<SendTestEmailResult> {
    return this.client.post("/send_test_email", input);
  }

  async setActiveBillingProduct(input: SetActiveBillingProductInput): Promise<SetActiveBillingProductResult> {
    return this.client.post("/set_active_billing_product", input);
  }

  async startSearchIndexMaintenance(
    input: StartSearchIndexMaintenanceInput,
  ): Promise<StartSearchIndexMaintenanceResult> {
    return this.client.post("/start_search_index_maintenance", input);
  }

  async syncBillingProductsFromPolar(
    input: SyncBillingProductsFromPolarInput,
  ): Promise<SyncBillingProductsFromPolarResult> {
    return this.client.post("/sync_billing_products_from_polar", input);
  }

  async unarchiveBillingPlanDefinition(
    input: UnarchiveBillingPlanDefinitionInput,
  ): Promise<UnarchiveBillingPlanDefinitionResult> {
    return this.client.post("/unarchive_billing_plan_definition", input);
  }

  async updateBillingPlanDefinition(
    input: UpdateBillingPlanDefinitionInput,
  ): Promise<UpdateBillingPlanDefinitionResult> {
    return this.client.post("/update_billing_plan_definition", input);
  }

  async updateBillingProduct(input: UpdateBillingProductInput): Promise<UpdateBillingProductResult> {
    return this.client.post("/update_billing_product", input);
  }

  async updateEmailSettings(input: UpdateEmailSettingsInput): Promise<UpdateEmailSettingsResult> {
    return this.client.post("/update_email_settings", input);
  }

  async updateSiteMessage(input: UpdateSiteMessageInput): Promise<UpdateSiteMessageResult> {
    return this.client.post("/update_site_message", input);
  }

  async updateUpdateBadgeSettings(input: UpdateUpdateBadgeSettingsInput): Promise<UpdateUpdateBadgeSettingsResult> {
    return this.client.post("/update_update_badge_settings", input);
  }
}

export class ApiClient {
  private basePath: string;
  private headers: any;
  public apiNamespaceCuratedTemplates: ApiNamespaceCuratedTemplates;
  public apiNamespaceRoot: ApiNamespaceRoot;

  constructor() {
    this.apiNamespaceCuratedTemplates = new ApiNamespaceCuratedTemplates(this);
    this.apiNamespaceRoot = new ApiNamespaceRoot(this);
  }

  setBasePath(basePath: string) {
    this.basePath = basePath;
  }

  getBasePath() {
    if (!this.basePath) throw new Error("ApiClient is not configured");
    return this.basePath;
  }

  setHeaders(headers: any) {
    this.headers = headers;
  }

  getHeaders() {
    return this.headers || {};
  }

  // @ts-ignore
  async post(path: string, data: any) {
    try {
      const response = await axios.post(this.getBasePath() + path, toSnake(data), { headers: this.getHeaders() });
      return toCamel(response.data);
    } catch (error) {
      handleStaleClientError(error);
      throw error;
    }
  }

  // @ts-ignore
  async get(path: string, params: any, basePath = this.getBasePath(), headers = this.getHeaders()) {
    try {
      return await this.queryRequest(path, params, basePath, headers);
    } catch (error) {
      handleStaleClientError(error);
      throw error;
    }
  }

  // Cached queries report errors at their caller boundaries, never during speculation.
  async queryRequest(path: string, params: any, basePath: string, headers: any) {
    const response = await axios.get(basePath + path, { params: toSnake(params), headers });
    return toCamel(response.data);
  }

  archiveBillingPlanDefinition(input: ArchiveBillingPlanDefinitionInput): Promise<ArchiveBillingPlanDefinitionResult> {
    return this.apiNamespaceRoot.archiveBillingPlanDefinition(input);
  }

  archiveBillingProduct(input: ArchiveBillingProductInput): Promise<ArchiveBillingProductResult> {
    return this.apiNamespaceRoot.archiveBillingProduct(input);
  }

  createBillingPlanDefinition(input: CreateBillingPlanDefinitionInput): Promise<CreateBillingPlanDefinitionResult> {
    return this.apiNamespaceRoot.createBillingPlanDefinition(input);
  }

  createBillingProduct(input: CreateBillingProductInput): Promise<CreateBillingProductResult> {
    return this.apiNamespaceRoot.createBillingProduct(input);
  }

  createSiteMessage(input: CreateSiteMessageInput): Promise<CreateSiteMessageResult> {
    return this.apiNamespaceRoot.createSiteMessage(input);
  }

  deleteAccount(input: DeleteAccountInput): Promise<DeleteAccountResult> {
    return this.apiNamespaceRoot.deleteAccount(input);
  }

  deleteSiteMessage(input: DeleteSiteMessageInput): Promise<DeleteSiteMessageResult> {
    return this.apiNamespaceRoot.deleteSiteMessage(input);
  }

  demoteAccountFromSiteAdmin(input: DemoteAccountFromSiteAdminInput): Promise<DemoteAccountFromSiteAdminResult> {
    return this.apiNamespaceRoot.demoteAccountFromSiteAdmin(input);
  }

  disableFeatures(input: DisableFeaturesInput): Promise<DisableFeaturesResult> {
    return this.apiNamespaceRoot.disableFeatures(input);
  }

  enableFeature(input: EnableFeatureInput): Promise<EnableFeatureResult> {
    return this.apiNamespaceRoot.enableFeature(input);
  }

  promoteAccountToSiteAdmin(input: PromoteAccountToSiteAdminInput): Promise<PromoteAccountToSiteAdminResult> {
    return this.apiNamespaceRoot.promoteAccountToSiteAdmin(input);
  }

  sendTestEmail(input: SendTestEmailInput): Promise<SendTestEmailResult> {
    return this.apiNamespaceRoot.sendTestEmail(input);
  }

  setActiveBillingProduct(input: SetActiveBillingProductInput): Promise<SetActiveBillingProductResult> {
    return this.apiNamespaceRoot.setActiveBillingProduct(input);
  }

  startSearchIndexMaintenance(input: StartSearchIndexMaintenanceInput): Promise<StartSearchIndexMaintenanceResult> {
    return this.apiNamespaceRoot.startSearchIndexMaintenance(input);
  }

  syncBillingProductsFromPolar(input: SyncBillingProductsFromPolarInput): Promise<SyncBillingProductsFromPolarResult> {
    return this.apiNamespaceRoot.syncBillingProductsFromPolar(input);
  }

  unarchiveBillingPlanDefinition(
    input: UnarchiveBillingPlanDefinitionInput,
  ): Promise<UnarchiveBillingPlanDefinitionResult> {
    return this.apiNamespaceRoot.unarchiveBillingPlanDefinition(input);
  }

  updateBillingPlanDefinition(input: UpdateBillingPlanDefinitionInput): Promise<UpdateBillingPlanDefinitionResult> {
    return this.apiNamespaceRoot.updateBillingPlanDefinition(input);
  }

  updateBillingProduct(input: UpdateBillingProductInput): Promise<UpdateBillingProductResult> {
    return this.apiNamespaceRoot.updateBillingProduct(input);
  }

  updateEmailSettings(input: UpdateEmailSettingsInput): Promise<UpdateEmailSettingsResult> {
    return this.apiNamespaceRoot.updateEmailSettings(input);
  }

  updateSiteMessage(input: UpdateSiteMessageInput): Promise<UpdateSiteMessageResult> {
    return this.apiNamespaceRoot.updateSiteMessage(input);
  }

  updateUpdateBadgeSettings(input: UpdateUpdateBadgeSettingsInput): Promise<UpdateUpdateBadgeSettingsResult> {
    return this.apiNamespaceRoot.updateUpdateBadgeSettings(input);
  }
}

function buildApiQueryKeyPrefix(client: ApiClient, path: string) {
  return ["operately-api", client.getBasePath(), { ...client.getHeaders() }, path] as const;
}

function buildApiQueryKey<InputT>(client: ApiClient, path: string, input: InputT) {
  return [...buildApiQueryKeyPrefix(client, path), input] as const;
}

function buildApiQueryOptions<InputT, ResultT>(client: ApiClient, path: string, input: InputT) {
  return queryOptions({
    queryKey: buildApiQueryKey(client, path, input),
    // Read the effective key so layout scope overrides also apply to the request.
    queryFn: ({ queryKey: [, basePath, headers, queryPath, queryInput] }): Promise<ResultT> =>
      client.queryRequest(queryPath, queryInput, basePath, headers),
  });
}

const defaultApiClient = new ApiClient();

export async function archiveBillingPlanDefinition(
  input: ArchiveBillingPlanDefinitionInput,
): Promise<ArchiveBillingPlanDefinitionResult> {
  return defaultApiClient.archiveBillingPlanDefinition(input);
}
export async function archiveBillingProduct(input: ArchiveBillingProductInput): Promise<ArchiveBillingProductResult> {
  return defaultApiClient.archiveBillingProduct(input);
}
export async function createBillingPlanDefinition(
  input: CreateBillingPlanDefinitionInput,
): Promise<CreateBillingPlanDefinitionResult> {
  return defaultApiClient.createBillingPlanDefinition(input);
}
export async function createBillingProduct(input: CreateBillingProductInput): Promise<CreateBillingProductResult> {
  return defaultApiClient.createBillingProduct(input);
}
export async function createSiteMessage(input: CreateSiteMessageInput): Promise<CreateSiteMessageResult> {
  return defaultApiClient.createSiteMessage(input);
}
export async function deleteAccount(input: DeleteAccountInput): Promise<DeleteAccountResult> {
  return defaultApiClient.deleteAccount(input);
}
export async function deleteSiteMessage(input: DeleteSiteMessageInput): Promise<DeleteSiteMessageResult> {
  return defaultApiClient.deleteSiteMessage(input);
}
export async function demoteAccountFromSiteAdmin(
  input: DemoteAccountFromSiteAdminInput,
): Promise<DemoteAccountFromSiteAdminResult> {
  return defaultApiClient.demoteAccountFromSiteAdmin(input);
}
export async function disableFeatures(input: DisableFeaturesInput): Promise<DisableFeaturesResult> {
  return defaultApiClient.disableFeatures(input);
}
export async function enableFeature(input: EnableFeatureInput): Promise<EnableFeatureResult> {
  return defaultApiClient.enableFeature(input);
}
export async function promoteAccountToSiteAdmin(
  input: PromoteAccountToSiteAdminInput,
): Promise<PromoteAccountToSiteAdminResult> {
  return defaultApiClient.promoteAccountToSiteAdmin(input);
}
export async function sendTestEmail(input: SendTestEmailInput): Promise<SendTestEmailResult> {
  return defaultApiClient.sendTestEmail(input);
}
export async function setActiveBillingProduct(
  input: SetActiveBillingProductInput,
): Promise<SetActiveBillingProductResult> {
  return defaultApiClient.setActiveBillingProduct(input);
}
export async function startSearchIndexMaintenance(
  input: StartSearchIndexMaintenanceInput,
): Promise<StartSearchIndexMaintenanceResult> {
  return defaultApiClient.startSearchIndexMaintenance(input);
}
export async function syncBillingProductsFromPolar(
  input: SyncBillingProductsFromPolarInput,
): Promise<SyncBillingProductsFromPolarResult> {
  return defaultApiClient.syncBillingProductsFromPolar(input);
}
export async function unarchiveBillingPlanDefinition(
  input: UnarchiveBillingPlanDefinitionInput,
): Promise<UnarchiveBillingPlanDefinitionResult> {
  return defaultApiClient.unarchiveBillingPlanDefinition(input);
}
export async function updateBillingPlanDefinition(
  input: UpdateBillingPlanDefinitionInput,
): Promise<UpdateBillingPlanDefinitionResult> {
  return defaultApiClient.updateBillingPlanDefinition(input);
}
export async function updateBillingProduct(input: UpdateBillingProductInput): Promise<UpdateBillingProductResult> {
  return defaultApiClient.updateBillingProduct(input);
}
export async function updateEmailSettings(input: UpdateEmailSettingsInput): Promise<UpdateEmailSettingsResult> {
  return defaultApiClient.updateEmailSettings(input);
}
export async function updateSiteMessage(input: UpdateSiteMessageInput): Promise<UpdateSiteMessageResult> {
  return defaultApiClient.updateSiteMessage(input);
}
export async function updateUpdateBadgeSettings(
  input: UpdateUpdateBadgeSettingsInput,
): Promise<UpdateUpdateBadgeSettingsResult> {
  return defaultApiClient.updateUpdateBadgeSettings(input);
}

export function getAccountsQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_accounts");
}

export function getAccountsQueryKey(input: GetAccountsInput) {
  return buildApiQueryKey(defaultApiClient, "/get_accounts", input);
}

export function getAccountsQueryOptions(input: GetAccountsInput) {
  return buildApiQueryOptions<GetAccountsInput, GetAccountsResult>(defaultApiClient, "/get_accounts", input);
}

export function getAccountsQuery(input: GetAccountsInput) {
  return queryClient.query({
    ...getAccountsQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getActiveCompaniesQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_active_companies");
}

export function getActiveCompaniesQueryKey(input: GetActiveCompaniesInput) {
  return buildApiQueryKey(defaultApiClient, "/get_active_companies", input);
}

export function getActiveCompaniesQueryOptions(input: GetActiveCompaniesInput) {
  return buildApiQueryOptions<GetActiveCompaniesInput, GetActiveCompaniesResult>(
    defaultApiClient,
    "/get_active_companies",
    input,
  );
}

export function getActiveCompaniesQuery(input: GetActiveCompaniesInput) {
  return queryClient.query({
    ...getActiveCompaniesQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getActivitiesQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_activities");
}

export function getActivitiesQueryKey(input: GetActivitiesInput) {
  return buildApiQueryKey(defaultApiClient, "/get_activities", input);
}

export function getActivitiesQueryOptions(input: GetActivitiesInput) {
  return buildApiQueryOptions<GetActivitiesInput, GetActivitiesResult>(defaultApiClient, "/get_activities", input);
}

export function getActivitiesQuery(input: GetActivitiesInput) {
  return queryClient.query({
    ...getActivitiesQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getCompaniesQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_companies");
}

export function getCompaniesQueryKey(input: GetCompaniesInput) {
  return buildApiQueryKey(defaultApiClient, "/get_companies", input);
}

export function getCompaniesQueryOptions(input: GetCompaniesInput) {
  return buildApiQueryOptions<GetCompaniesInput, GetCompaniesResult>(defaultApiClient, "/get_companies", input);
}

export function getCompaniesQuery(input: GetCompaniesInput) {
  return queryClient.query({
    ...getCompaniesQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getCompanyQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_company");
}

export function getCompanyQueryKey(input: GetCompanyInput) {
  return buildApiQueryKey(defaultApiClient, "/get_company", input);
}

export function getCompanyQueryOptions(input: GetCompanyInput) {
  return buildApiQueryOptions<GetCompanyInput, GetCompanyResult>(defaultApiClient, "/get_company", input);
}

export function getCompanyQuery(input: GetCompanyInput) {
  return queryClient.query({
    ...getCompanyQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getEmailSettingsQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_email_settings");
}

export function getEmailSettingsQueryKey(input: GetEmailSettingsInput) {
  return buildApiQueryKey(defaultApiClient, "/get_email_settings", input);
}

export function getEmailSettingsQueryOptions(input: GetEmailSettingsInput) {
  return buildApiQueryOptions<GetEmailSettingsInput, GetEmailSettingsResult>(
    defaultApiClient,
    "/get_email_settings",
    input,
  );
}

export function getEmailSettingsQuery(input: GetEmailSettingsInput) {
  return queryClient.query({
    ...getEmailSettingsQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getSearchIndexStatusQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_search_index_status");
}

export function getSearchIndexStatusQueryKey(input: GetSearchIndexStatusInput) {
  return buildApiQueryKey(defaultApiClient, "/get_search_index_status", input);
}

export function getSearchIndexStatusQueryOptions(input: GetSearchIndexStatusInput) {
  return buildApiQueryOptions<GetSearchIndexStatusInput, GetSearchIndexStatusResult>(
    defaultApiClient,
    "/get_search_index_status",
    input,
  );
}

export function getSearchIndexStatusQuery(input: GetSearchIndexStatusInput) {
  return queryClient.query({
    ...getSearchIndexStatusQueryOptions(input),
    staleTime: Infinity,
  });
}

export function getUpdateBadgeSettingsQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/get_update_badge_settings");
}

export function getUpdateBadgeSettingsQueryKey(input: GetUpdateBadgeSettingsInput) {
  return buildApiQueryKey(defaultApiClient, "/get_update_badge_settings", input);
}

export function getUpdateBadgeSettingsQueryOptions(input: GetUpdateBadgeSettingsInput) {
  return buildApiQueryOptions<GetUpdateBadgeSettingsInput, GetUpdateBadgeSettingsResult>(
    defaultApiClient,
    "/get_update_badge_settings",
    input,
  );
}

export function getUpdateBadgeSettingsQuery(input: GetUpdateBadgeSettingsInput) {
  return queryClient.query({
    ...getUpdateBadgeSettingsQueryOptions(input),
    staleTime: Infinity,
  });
}

export function listBillingPlanDefinitionsQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/list_billing_plan_definitions");
}

export function listBillingPlanDefinitionsQueryKey(input: ListBillingPlanDefinitionsInput) {
  return buildApiQueryKey(defaultApiClient, "/list_billing_plan_definitions", input);
}

export function listBillingPlanDefinitionsQueryOptions(input: ListBillingPlanDefinitionsInput) {
  return buildApiQueryOptions<ListBillingPlanDefinitionsInput, ListBillingPlanDefinitionsResult>(
    defaultApiClient,
    "/list_billing_plan_definitions",
    input,
  );
}

export function listBillingPlanDefinitionsQuery(input: ListBillingPlanDefinitionsInput) {
  return queryClient.query({
    ...listBillingPlanDefinitionsQueryOptions(input),
    staleTime: Infinity,
  });
}

export function listBillingProductsQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/list_billing_products");
}

export function listBillingProductsQueryKey(input: ListBillingProductsInput) {
  return buildApiQueryKey(defaultApiClient, "/list_billing_products", input);
}

export function listBillingProductsQueryOptions(input: ListBillingProductsInput) {
  return buildApiQueryOptions<ListBillingProductsInput, ListBillingProductsResult>(
    defaultApiClient,
    "/list_billing_products",
    input,
  );
}

export function listBillingProductsQuery(input: ListBillingProductsInput) {
  return queryClient.query({
    ...listBillingProductsQueryOptions(input),
    staleTime: Infinity,
  });
}

export function listSiteMessagesQueryKeyPrefix() {
  return buildApiQueryKeyPrefix(defaultApiClient, "/list_site_messages");
}

export function listSiteMessagesQueryKey(input: ListSiteMessagesInput) {
  return buildApiQueryKey(defaultApiClient, "/list_site_messages", input);
}

export function listSiteMessagesQueryOptions(input: ListSiteMessagesInput) {
  return buildApiQueryOptions<ListSiteMessagesInput, ListSiteMessagesResult>(
    defaultApiClient,
    "/list_site_messages",
    input,
  );
}

export function listSiteMessagesQuery(input: ListSiteMessagesInput) {
  return queryClient.query({
    ...listSiteMessagesQueryOptions(input),
    staleTime: Infinity,
  });
}

export function archiveBillingPlanDefinitionMutationOptions() {
  return mutationOptions({
    mutationFn: (input: ArchiveBillingPlanDefinitionInput) => defaultApiClient.archiveBillingPlanDefinition(input),
  });
}

export function archiveBillingProductMutationOptions() {
  return mutationOptions({
    mutationFn: (input: ArchiveBillingProductInput) => defaultApiClient.archiveBillingProduct(input),
  });
}

export function createBillingPlanDefinitionMutationOptions() {
  return mutationOptions({
    mutationFn: (input: CreateBillingPlanDefinitionInput) => defaultApiClient.createBillingPlanDefinition(input),
  });
}

export function createBillingProductMutationOptions() {
  return mutationOptions({
    mutationFn: (input: CreateBillingProductInput) => defaultApiClient.createBillingProduct(input),
  });
}

export function createSiteMessageMutationOptions() {
  return mutationOptions({
    mutationFn: (input: CreateSiteMessageInput) => defaultApiClient.createSiteMessage(input),
  });
}

export function deleteAccountMutationOptions() {
  return mutationOptions({
    mutationFn: (input: DeleteAccountInput) => defaultApiClient.deleteAccount(input),
  });
}

export function deleteSiteMessageMutationOptions() {
  return mutationOptions({
    mutationFn: (input: DeleteSiteMessageInput) => defaultApiClient.deleteSiteMessage(input),
  });
}

export function demoteAccountFromSiteAdminMutationOptions() {
  return mutationOptions({
    mutationFn: (input: DemoteAccountFromSiteAdminInput) => defaultApiClient.demoteAccountFromSiteAdmin(input),
  });
}

export function disableFeaturesMutationOptions() {
  return mutationOptions({
    mutationFn: (input: DisableFeaturesInput) => defaultApiClient.disableFeatures(input),
  });
}

export function enableFeatureMutationOptions() {
  return mutationOptions({
    mutationFn: (input: EnableFeatureInput) => defaultApiClient.enableFeature(input),
  });
}

export function promoteAccountToSiteAdminMutationOptions() {
  return mutationOptions({
    mutationFn: (input: PromoteAccountToSiteAdminInput) => defaultApiClient.promoteAccountToSiteAdmin(input),
  });
}

export function sendTestEmailMutationOptions() {
  return mutationOptions({
    mutationFn: (input: SendTestEmailInput) => defaultApiClient.sendTestEmail(input),
  });
}

export function setActiveBillingProductMutationOptions() {
  return mutationOptions({
    mutationFn: (input: SetActiveBillingProductInput) => defaultApiClient.setActiveBillingProduct(input),
  });
}

export function startSearchIndexMaintenanceMutationOptions() {
  return mutationOptions({
    mutationFn: (input: StartSearchIndexMaintenanceInput) => defaultApiClient.startSearchIndexMaintenance(input),
  });
}

export function syncBillingProductsFromPolarMutationOptions() {
  return mutationOptions({
    mutationFn: (input: SyncBillingProductsFromPolarInput) => defaultApiClient.syncBillingProductsFromPolar(input),
  });
}

export function unarchiveBillingPlanDefinitionMutationOptions() {
  return mutationOptions({
    mutationFn: (input: UnarchiveBillingPlanDefinitionInput) => defaultApiClient.unarchiveBillingPlanDefinition(input),
  });
}

export function updateBillingPlanDefinitionMutationOptions() {
  return mutationOptions({
    mutationFn: (input: UpdateBillingPlanDefinitionInput) => defaultApiClient.updateBillingPlanDefinition(input),
  });
}

export function updateBillingProductMutationOptions() {
  return mutationOptions({
    mutationFn: (input: UpdateBillingProductInput) => defaultApiClient.updateBillingProduct(input),
  });
}

export function updateEmailSettingsMutationOptions() {
  return mutationOptions({
    mutationFn: (input: UpdateEmailSettingsInput) => defaultApiClient.updateEmailSettings(input),
  });
}

export function updateSiteMessageMutationOptions() {
  return mutationOptions({
    mutationFn: (input: UpdateSiteMessageInput) => defaultApiClient.updateSiteMessage(input),
  });
}

export function updateUpdateBadgeSettingsMutationOptions() {
  return mutationOptions({
    mutationFn: (input: UpdateUpdateBadgeSettingsInput) => defaultApiClient.updateUpdateBadgeSettings(input),
  });
}

export function useArchiveBillingPlanDefinition(): UseMutationHookResult<
  ArchiveBillingPlanDefinitionInput,
  ArchiveBillingPlanDefinitionResult
> {
  return useMutation<ArchiveBillingPlanDefinitionInput, ArchiveBillingPlanDefinitionResult>((input) =>
    defaultApiClient.archiveBillingPlanDefinition(input),
  );
}

export function useArchiveBillingProduct(): UseMutationHookResult<
  ArchiveBillingProductInput,
  ArchiveBillingProductResult
> {
  return useMutation<ArchiveBillingProductInput, ArchiveBillingProductResult>((input) =>
    defaultApiClient.archiveBillingProduct(input),
  );
}

export function useCreateBillingPlanDefinition(): UseMutationHookResult<
  CreateBillingPlanDefinitionInput,
  CreateBillingPlanDefinitionResult
> {
  return useMutation<CreateBillingPlanDefinitionInput, CreateBillingPlanDefinitionResult>((input) =>
    defaultApiClient.createBillingPlanDefinition(input),
  );
}

export function useCreateBillingProduct(): UseMutationHookResult<
  CreateBillingProductInput,
  CreateBillingProductResult
> {
  return useMutation<CreateBillingProductInput, CreateBillingProductResult>((input) =>
    defaultApiClient.createBillingProduct(input),
  );
}

export function useCreateSiteMessage(): UseMutationHookResult<CreateSiteMessageInput, CreateSiteMessageResult> {
  return useMutation<CreateSiteMessageInput, CreateSiteMessageResult>((input) =>
    defaultApiClient.createSiteMessage(input),
  );
}

export function useDeleteAccount(): UseMutationHookResult<DeleteAccountInput, DeleteAccountResult> {
  return useMutation<DeleteAccountInput, DeleteAccountResult>((input) => defaultApiClient.deleteAccount(input));
}

export function useDeleteSiteMessage(): UseMutationHookResult<DeleteSiteMessageInput, DeleteSiteMessageResult> {
  return useMutation<DeleteSiteMessageInput, DeleteSiteMessageResult>((input) =>
    defaultApiClient.deleteSiteMessage(input),
  );
}

export function useDemoteAccountFromSiteAdmin(): UseMutationHookResult<
  DemoteAccountFromSiteAdminInput,
  DemoteAccountFromSiteAdminResult
> {
  return useMutation<DemoteAccountFromSiteAdminInput, DemoteAccountFromSiteAdminResult>((input) =>
    defaultApiClient.demoteAccountFromSiteAdmin(input),
  );
}

export function useDisableFeatures(): UseMutationHookResult<DisableFeaturesInput, DisableFeaturesResult> {
  return useMutation<DisableFeaturesInput, DisableFeaturesResult>((input) => defaultApiClient.disableFeatures(input));
}

export function useEnableFeature(): UseMutationHookResult<EnableFeatureInput, EnableFeatureResult> {
  return useMutation<EnableFeatureInput, EnableFeatureResult>((input) => defaultApiClient.enableFeature(input));
}

export function usePromoteAccountToSiteAdmin(): UseMutationHookResult<
  PromoteAccountToSiteAdminInput,
  PromoteAccountToSiteAdminResult
> {
  return useMutation<PromoteAccountToSiteAdminInput, PromoteAccountToSiteAdminResult>((input) =>
    defaultApiClient.promoteAccountToSiteAdmin(input),
  );
}

export function useSendTestEmail(): UseMutationHookResult<SendTestEmailInput, SendTestEmailResult> {
  return useMutation<SendTestEmailInput, SendTestEmailResult>((input) => defaultApiClient.sendTestEmail(input));
}

export function useSetActiveBillingProduct(): UseMutationHookResult<
  SetActiveBillingProductInput,
  SetActiveBillingProductResult
> {
  return useMutation<SetActiveBillingProductInput, SetActiveBillingProductResult>((input) =>
    defaultApiClient.setActiveBillingProduct(input),
  );
}

export function useStartSearchIndexMaintenance(): UseMutationHookResult<
  StartSearchIndexMaintenanceInput,
  StartSearchIndexMaintenanceResult
> {
  return useMutation<StartSearchIndexMaintenanceInput, StartSearchIndexMaintenanceResult>((input) =>
    defaultApiClient.startSearchIndexMaintenance(input),
  );
}

export function useSyncBillingProductsFromPolar(): UseMutationHookResult<
  SyncBillingProductsFromPolarInput,
  SyncBillingProductsFromPolarResult
> {
  return useMutation<SyncBillingProductsFromPolarInput, SyncBillingProductsFromPolarResult>((input) =>
    defaultApiClient.syncBillingProductsFromPolar(input),
  );
}

export function useUnarchiveBillingPlanDefinition(): UseMutationHookResult<
  UnarchiveBillingPlanDefinitionInput,
  UnarchiveBillingPlanDefinitionResult
> {
  return useMutation<UnarchiveBillingPlanDefinitionInput, UnarchiveBillingPlanDefinitionResult>((input) =>
    defaultApiClient.unarchiveBillingPlanDefinition(input),
  );
}

export function useUpdateBillingPlanDefinition(): UseMutationHookResult<
  UpdateBillingPlanDefinitionInput,
  UpdateBillingPlanDefinitionResult
> {
  return useMutation<UpdateBillingPlanDefinitionInput, UpdateBillingPlanDefinitionResult>((input) =>
    defaultApiClient.updateBillingPlanDefinition(input),
  );
}

export function useUpdateBillingProduct(): UseMutationHookResult<
  UpdateBillingProductInput,
  UpdateBillingProductResult
> {
  return useMutation<UpdateBillingProductInput, UpdateBillingProductResult>((input) =>
    defaultApiClient.updateBillingProduct(input),
  );
}

export function useUpdateEmailSettings(): UseMutationHookResult<UpdateEmailSettingsInput, UpdateEmailSettingsResult> {
  return useMutation<UpdateEmailSettingsInput, UpdateEmailSettingsResult>((input) =>
    defaultApiClient.updateEmailSettings(input),
  );
}

export function useUpdateSiteMessage(): UseMutationHookResult<UpdateSiteMessageInput, UpdateSiteMessageResult> {
  return useMutation<UpdateSiteMessageInput, UpdateSiteMessageResult>((input) =>
    defaultApiClient.updateSiteMessage(input),
  );
}

export function useUpdateUpdateBadgeSettings(): UseMutationHookResult<
  UpdateUpdateBadgeSettingsInput,
  UpdateUpdateBadgeSettingsResult
> {
  return useMutation<UpdateUpdateBadgeSettingsInput, UpdateUpdateBadgeSettingsResult>((input) =>
    defaultApiClient.updateUpdateBadgeSettings(input),
  );
}

export default {
  default: defaultApiClient,

  getAccountsQueryKeyPrefix,
  getAccountsQueryKey,
  getAccountsQueryOptions,
  getAccountsQuery,
  getActiveCompaniesQueryKeyPrefix,
  getActiveCompaniesQueryKey,
  getActiveCompaniesQueryOptions,
  getActiveCompaniesQuery,
  getActivitiesQueryKeyPrefix,
  getActivitiesQueryKey,
  getActivitiesQueryOptions,
  getActivitiesQuery,
  getCompaniesQueryKeyPrefix,
  getCompaniesQueryKey,
  getCompaniesQueryOptions,
  getCompaniesQuery,
  getCompanyQueryKeyPrefix,
  getCompanyQueryKey,
  getCompanyQueryOptions,
  getCompanyQuery,
  getEmailSettingsQueryKeyPrefix,
  getEmailSettingsQueryKey,
  getEmailSettingsQueryOptions,
  getEmailSettingsQuery,
  getSearchIndexStatusQueryKeyPrefix,
  getSearchIndexStatusQueryKey,
  getSearchIndexStatusQueryOptions,
  getSearchIndexStatusQuery,
  getUpdateBadgeSettingsQueryKeyPrefix,
  getUpdateBadgeSettingsQueryKey,
  getUpdateBadgeSettingsQueryOptions,
  getUpdateBadgeSettingsQuery,
  listBillingPlanDefinitionsQueryKeyPrefix,
  listBillingPlanDefinitionsQueryKey,
  listBillingPlanDefinitionsQueryOptions,
  listBillingPlanDefinitionsQuery,
  listBillingProductsQueryKeyPrefix,
  listBillingProductsQueryKey,
  listBillingProductsQueryOptions,
  listBillingProductsQuery,
  listSiteMessagesQueryKeyPrefix,
  listSiteMessagesQueryKey,
  listSiteMessagesQueryOptions,
  listSiteMessagesQuery,
  archiveBillingPlanDefinition,
  useArchiveBillingPlanDefinition,
  archiveBillingPlanDefinitionMutationOptions,
  archiveBillingProduct,
  useArchiveBillingProduct,
  archiveBillingProductMutationOptions,
  createBillingPlanDefinition,
  useCreateBillingPlanDefinition,
  createBillingPlanDefinitionMutationOptions,
  createBillingProduct,
  useCreateBillingProduct,
  createBillingProductMutationOptions,
  createSiteMessage,
  useCreateSiteMessage,
  createSiteMessageMutationOptions,
  deleteAccount,
  useDeleteAccount,
  deleteAccountMutationOptions,
  deleteSiteMessage,
  useDeleteSiteMessage,
  deleteSiteMessageMutationOptions,
  demoteAccountFromSiteAdmin,
  useDemoteAccountFromSiteAdmin,
  demoteAccountFromSiteAdminMutationOptions,
  disableFeatures,
  useDisableFeatures,
  disableFeaturesMutationOptions,
  enableFeature,
  useEnableFeature,
  enableFeatureMutationOptions,
  promoteAccountToSiteAdmin,
  usePromoteAccountToSiteAdmin,
  promoteAccountToSiteAdminMutationOptions,
  sendTestEmail,
  useSendTestEmail,
  sendTestEmailMutationOptions,
  setActiveBillingProduct,
  useSetActiveBillingProduct,
  setActiveBillingProductMutationOptions,
  startSearchIndexMaintenance,
  useStartSearchIndexMaintenance,
  startSearchIndexMaintenanceMutationOptions,
  syncBillingProductsFromPolar,
  useSyncBillingProductsFromPolar,
  syncBillingProductsFromPolarMutationOptions,
  unarchiveBillingPlanDefinition,
  useUnarchiveBillingPlanDefinition,
  unarchiveBillingPlanDefinitionMutationOptions,
  updateBillingPlanDefinition,
  useUpdateBillingPlanDefinition,
  updateBillingPlanDefinitionMutationOptions,
  updateBillingProduct,
  useUpdateBillingProduct,
  updateBillingProductMutationOptions,
  updateEmailSettings,
  useUpdateEmailSettings,
  updateEmailSettingsMutationOptions,
  updateSiteMessage,
  useUpdateSiteMessage,
  updateSiteMessageMutationOptions,
  updateUpdateBadgeSettings,
  useUpdateUpdateBadgeSettings,
  updateUpdateBadgeSettingsMutationOptions,

  curated_templates: {
    getQueryKeyPrefix: () => buildApiQueryKeyPrefix(defaultApiClient, "/curated_templates/get"),
    getQueryKey: (input: CuratedTemplatesGetInput) =>
      buildApiQueryKey(defaultApiClient, "/curated_templates/get", input),
    getQueryOptions: (input: CuratedTemplatesGetInput) =>
      buildApiQueryOptions<CuratedTemplatesGetInput, CuratedTemplatesGetResult>(
        defaultApiClient,
        "/curated_templates/get",
        input,
      ),
    getQuery: (input: CuratedTemplatesGetInput) =>
      queryClient.query({
        ...buildApiQueryOptions<CuratedTemplatesGetInput, CuratedTemplatesGetResult>(
          defaultApiClient,
          "/curated_templates/get",
          input,
        ),
        staleTime: Infinity,
      }),

    listQueryKeyPrefix: () => buildApiQueryKeyPrefix(defaultApiClient, "/curated_templates/list"),
    listQueryKey: (input: CuratedTemplatesListInput) =>
      buildApiQueryKey(defaultApiClient, "/curated_templates/list", input),
    listQueryOptions: (input: CuratedTemplatesListInput) =>
      buildApiQueryOptions<CuratedTemplatesListInput, CuratedTemplatesListResult>(
        defaultApiClient,
        "/curated_templates/list",
        input,
      ),
    listQuery: (input: CuratedTemplatesListInput) =>
      queryClient.query({
        ...buildApiQueryOptions<CuratedTemplatesListInput, CuratedTemplatesListResult>(
          defaultApiClient,
          "/curated_templates/list",
          input,
        ),
        staleTime: Infinity,
      }),

    create: (input: CuratedTemplatesCreateInput) => defaultApiClient.apiNamespaceCuratedTemplates.create(input),
    useCreate: () =>
      useMutation<CuratedTemplatesCreateInput, CuratedTemplatesCreateResult>((input) =>
        defaultApiClient.apiNamespaceCuratedTemplates.create(input),
      ),
    createMutationOptions: () =>
      mutationOptions({
        mutationFn: (input: CuratedTemplatesCreateInput) => defaultApiClient.apiNamespaceCuratedTemplates.create(input),
      }),

    delete: (input: CuratedTemplatesDeleteInput) => defaultApiClient.apiNamespaceCuratedTemplates.delete(input),
    useDelete: () =>
      useMutation<CuratedTemplatesDeleteInput, CuratedTemplatesDeleteResult>((input) =>
        defaultApiClient.apiNamespaceCuratedTemplates.delete(input),
      ),
    deleteMutationOptions: () =>
      mutationOptions({
        mutationFn: (input: CuratedTemplatesDeleteInput) => defaultApiClient.apiNamespaceCuratedTemplates.delete(input),
      }),

    publish: (input: CuratedTemplatesPublishInput) => defaultApiClient.apiNamespaceCuratedTemplates.publish(input),
    usePublish: () =>
      useMutation<CuratedTemplatesPublishInput, CuratedTemplatesPublishResult>((input) =>
        defaultApiClient.apiNamespaceCuratedTemplates.publish(input),
      ),
    publishMutationOptions: () =>
      mutationOptions({
        mutationFn: (input: CuratedTemplatesPublishInput) =>
          defaultApiClient.apiNamespaceCuratedTemplates.publish(input),
      }),

    update: (input: CuratedTemplatesUpdateInput) => defaultApiClient.apiNamespaceCuratedTemplates.update(input),
    useUpdate: () =>
      useMutation<CuratedTemplatesUpdateInput, CuratedTemplatesUpdateResult>((input) =>
        defaultApiClient.apiNamespaceCuratedTemplates.update(input),
      ),
    updateMutationOptions: () =>
      mutationOptions({
        mutationFn: (input: CuratedTemplatesUpdateInput) => defaultApiClient.apiNamespaceCuratedTemplates.update(input),
      }),

    updateMetadata: (input: CuratedTemplatesUpdateMetadataInput) =>
      defaultApiClient.apiNamespaceCuratedTemplates.updateMetadata(input),
    useUpdateMetadata: () =>
      useMutation<CuratedTemplatesUpdateMetadataInput, CuratedTemplatesUpdateMetadataResult>((input) =>
        defaultApiClient.apiNamespaceCuratedTemplates.updateMetadata(input),
      ),
    updateMetadataMutationOptions: () =>
      mutationOptions({
        mutationFn: (input: CuratedTemplatesUpdateMetadataInput) =>
          defaultApiClient.apiNamespaceCuratedTemplates.updateMetadata(input),
      }),

    validate: (input: CuratedTemplatesValidateInput) => defaultApiClient.apiNamespaceCuratedTemplates.validate(input),
    useValidate: () =>
      useMutation<CuratedTemplatesValidateInput, CuratedTemplatesValidateResult>((input) =>
        defaultApiClient.apiNamespaceCuratedTemplates.validate(input),
      ),
    validateMutationOptions: () =>
      mutationOptions({
        mutationFn: (input: CuratedTemplatesValidateInput) =>
          defaultApiClient.apiNamespaceCuratedTemplates.validate(input),
      }),
  },
};
